import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { storage } from '../api/client';
import { authApi } from '../api/auth';

const AuthContext = createContext(null);

const extractPermissions = (data) => {
  if (data && Array.isArray(data.permissions)) return data.permissions;
  if (data && data.user && Array.isArray(data.user.permissions)) return data.user.permissions;
  return [];
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(storage.getUser());
  const [permissions, setPermissions] = useState(storage.getPermissions());
  const [impersonator, setImpersonator] = useState(storage.getImpersonator());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = storage.getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    authApi
      .me()
      .then(({ data }) => {
        setUser(data.user);
        setPermissions(extractPermissions(data));
        storage.setUser(data.user);
        storage.setPermissions(extractPermissions(data));
      })
      .catch(() => {
        storage.clearAll();
        setUser(null);
        setPermissions([]);
        setImpersonator(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await authApi.login({ email, password });
    storage.setToken(data.token);
    storage.setUser(data.user);
    storage.setPermissions(extractPermissions(data));
    setUser(data.user);
    setPermissions(extractPermissions(data));
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await authApi.register(payload);
    storage.setToken(data.token);
    storage.setUser(data.user);
    storage.setPermissions(extractPermissions(data));
    setUser(data.user);
    setPermissions(extractPermissions(data));
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    storage.clearAll();
    setUser(null);
    setPermissions([]);
    setImpersonator(null);
  }, []);

  const impersonate = useCallback(async (userId) => {
    const { data } = await authApi.impersonate(userId);
    storage.setImpersonator({
      token: data.impersonator_token,
      id: data.impersonator_id,
      name: data.impersonator_name,
      targetName: data.user.name,
    });
    setImpersonator({
      token: data.impersonator_token,
      id: data.impersonator_id,
      name: data.impersonator_name,
      targetName: data.user.name,
    });
    storage.setToken(data.token);
    storage.setUser(data.user);
    storage.setPermissions(extractPermissions(data));
    setUser(data.user);
    setPermissions(extractPermissions(data));
    return data;
  }, []);

  const stopImpersonation = useCallback(async () => {
    const { data } = await authApi.stopImpersonation(impersonator?.id);
    storage.clearImpersonator();
    storage.setToken(data.token);
    storage.setUser(data.user);
    storage.setPermissions(extractPermissions(data));
    setUser(data.user);
    setPermissions(extractPermissions(data));
    setImpersonator(null);
    return data;
  }, [impersonator?.id]);

  const isAdmin = useCallback(() => {
    const roles = user?.roles || [];
    return roles.some((r) => {
      const slug = typeof r === 'object' ? r.slug || r.name : String(r);
      return ['admin', 'developer'].includes(String(slug).toLowerCase());
    });
  }, [user]);

  const hasPermission = useCallback((perm) => {
    if (!perm) return true;
    if (isAdmin()) return true;
    return permissions.includes(perm);
  }, [isAdmin, permissions]);

  const value = {
    user,
    permissions,
    impersonator,
    isImpersonating: !!impersonator,
    setPermissions,
    setUser,
    login,
    register,
    logout,
    impersonate,
    stopImpersonation,
    hasPermission,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
